import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { getApiUrl } from '../config/api';
import { setAuthTokenInMemory, AUTH_SESSION_EXPIRED_EVENT } from '../utils/authUtils';

const SESSION_TOKEN_KEY = 'mmec_session_token';
const SESSION_EXPIRY_KEY = 'mmec_session_expiry';
const SESSION_USER_KEY = 'mmec_session_user';

/** Token valid until end of day: 24 hours from now (re-login required next day). */
function getSessionExpiryMs(): number {
  return Date.now() + 24 * 60 * 60 * 1000;
}

interface User {
  id: number;
  username: string;
  email: string;
  isAdmin?: boolean; // Add admin status
}

interface AuthContextType {
  isAuthenticated: boolean;
  user: User | null;
  token: string | null;
  loginWithToken: (token: string) => Promise<boolean>;
  logout: () => void;
  loading: boolean;
  isAdmin: boolean; // Add admin status getter
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export { AuthContext };

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  // Simple token verification function
  const verifyToken = async (tokenToVerify: string): Promise<boolean> => {
    try {
      // Check if we already have a valid token and user
      if (tokenToVerify === token && user && isAuthenticated) {
        return true; // Already authenticated with this token
      }

      // Set in-memory token BEFORE verify so any API call that runs right after login has the token (avoids race)
      setAuthTokenInMemory(tokenToVerify);

      const response = await fetch(getApiUrl('/api/auth/verify-flexible'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ token: tokenToVerify })
      });

      if (response.ok) {
        const data = await response.json();

        // Ensure we have user data. Persist in sessionStorage until end of day so refresh keeps user in.
        if (data.user) {
          setToken(tokenToVerify);
          setUser(data.user);
          setIsAuthenticated(true);
          const expiry = getSessionExpiryMs();
          try {
            sessionStorage.setItem(SESSION_TOKEN_KEY, tokenToVerify);
            sessionStorage.setItem(SESSION_EXPIRY_KEY, String(expiry));
            sessionStorage.setItem(SESSION_USER_KEY, JSON.stringify(data.user));
          } catch (_) {}
          return true;
        } else {
          console.error('No user data in response:', data);
          setAuthTokenInMemory(null);
          return false;
        }
      } else {
        console.error('Token verification failed with status:', response.status);
        setAuthTokenInMemory(null);
        return false;
      }
    } catch (error) {
      console.error('Token verification failed:', error);
      setAuthTokenInMemory(null);
      return false;
    }
  };

  // Token-based login: set in-memory token immediately so any request after this has it
  const loginWithToken = async (tokenToUse: string): Promise<boolean> => {
    setAuthTokenInMemory(tokenToUse);
    setLoading(true);
    const success = await verifyToken(tokenToUse);
    setLoading(false);
    return success;
  };

  // Logout: clear in-memory and session storage
  const logout = useCallback(() => {
    setAuthTokenInMemory(null);
    setToken(null);
    setUser(null);
    setIsAuthenticated(false);
    try {
      localStorage.removeItem('authToken');
      sessionStorage.removeItem(SESSION_TOKEN_KEY);
      sessionStorage.removeItem(SESSION_EXPIRY_KEY);
      sessionStorage.removeItem(SESSION_USER_KEY);
      sessionStorage.removeItem('authToken');
    } catch (_) {}
  }, []);

  // When any API path invalidates the token, clear local session so the app shows sign-in again (no silent retry loop).
  useEffect(() => {
    const onSessionExpired = () => {
      logout();
    };
    if (typeof window !== 'undefined') {
      window.addEventListener(AUTH_SESSION_EXPIRED_EVENT, onSessionExpired as EventListener);
    }
    return () => {
      if (typeof window !== 'undefined') {
        window.removeEventListener(AUTH_SESSION_EXPIRED_EVENT, onSessionExpired as EventListener);
      }
    };
  }, [logout]);

  // Restore session from sessionStorage if not expired (valid until end of day)
  useEffect(() => {
    try {
      const stored = sessionStorage.getItem(SESSION_TOKEN_KEY);
      const expiryStr = sessionStorage.getItem(SESSION_EXPIRY_KEY);
      const userStr = sessionStorage.getItem(SESSION_USER_KEY);
      const expiry = expiryStr ? parseInt(expiryStr, 10) : 0;
      if (stored && expiry > Date.now() && userStr) {
        const parsedUser = JSON.parse(userStr) as User;
        setAuthTokenInMemory(stored);
        setToken(stored);
        setUser(parsedUser);
        setIsAuthenticated(true);
      } else if (stored || expiryStr) {
        sessionStorage.removeItem(SESSION_TOKEN_KEY);
        sessionStorage.removeItem(SESSION_EXPIRY_KEY);
        sessionStorage.removeItem(SESSION_USER_KEY);
      }
    } catch (_) {}
    setLoading(false);
  }, []);

  const value: AuthContextType = {
    isAuthenticated,
    user,
    token,
    loginWithToken,
    logout,
    loading,
    isAdmin: user?.isAdmin || false // Add isAdmin getter
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}; 